import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { article as articleData, section as sectionData } from "scmp-app/data";
import type { typeSeriesArticleItemSeriesItemResult$key } from "scmp-app/queries/__generated__/typeSeriesArticleItemSeriesItemResult.graphql";

import {
  Container,
  Icon,
  SeriesType,
  StyledIconArticle,
  StyledIconInfographic,
  StyledIconOpinion,
  StyledIconPodcast,
  StyledIconVideo,
} from "./styles";

enum SeriesItemType {
  ARTICLE = "article",
  INFOGRAPHIC = "infographic",
  OPINION = "opinion",
  PODCAST = "podcast",
  VIDEO = "video",
}

type Props = {
  className?: string;
  reference: typeSeriesArticleItemSeriesItemResult$key;
};

export const SeriesArticleItemType: FunctionComponent<Props> = ({ className, reference }) => {
  const seriesItem = useFragment(
    graphql`
      fragment typeSeriesArticleItemSeriesItemResult on SeriesItemResult {
        ... on Article {
          __typename
          sections {
            value {
              entityId
            }
          }
          types {
            value {
              entityId
            }
          }
        }
        ... on Video {
          __typename
          duration
        }
        ... on Podcast {
          __typename
        }
      }
    `,
    reference,
  );

  const getSeriesType = () => {
    switch (seriesItem.__typename) {
      case "Article": {
        const articleTypes =
          seriesItem?.types?.flatMap(
            types => types?.value?.map(value => value?.entityId ?? "") ?? [],
          ) ?? [];

        const articleSections =
          seriesItem?.sections?.flatMap(
            sections => sections?.value?.map(value => value?.entityId ?? "") ?? [],
          ) ?? [];

        const isInfographic =
          (articleTypes?.some(id => id === articleData.types.infographic.entityId) ||
            articleSections?.some(id => id === sectionData.infographics.entityId)) ??
          false;
        if (isInfographic) return SeriesItemType.INFOGRAPHIC;

        const isOpinion =
          (articleTypes?.some(id => id === articleData.types.opinion.entityId) ||
            articleTypes?.some(id => id === articleData.types.column.entityId)) ??
          false;
        if (isOpinion) return SeriesItemType.OPINION;

        const isPodcast =
          (articleTypes?.some(id => id === articleData.types.podcast.entityId) ||
            articleSections?.some(id => id === sectionData.podcasts.entityId)) ??
          false;
        if (isPodcast) return SeriesItemType.PODCAST;

        return SeriesItemType.ARTICLE;
      }
      case "Video":
        return SeriesItemType.VIDEO;
      case "Podcast":
        return SeriesItemType.PODCAST;

      default:
        return null;
    }
  };

  const renderIcon = () => {
    switch (getSeriesType()) {
      case SeriesItemType.ARTICLE:
        return <StyledIconArticle />;

      case SeriesItemType.VIDEO:
        return <StyledIconVideo />;

      case SeriesItemType.PODCAST:
        return <StyledIconPodcast />;

      case SeriesItemType.INFOGRAPHIC:
        return <StyledIconInfographic />;

      case SeriesItemType.OPINION:
        return <StyledIconOpinion />;

      default:
        return null;
    }
  };

  const renderText = () => {
    const seriesType = getSeriesType();
    switch (seriesItem.__typename) {
      case "Video":
        if (seriesItem?.duration) {
          return `${seriesType} - ${seriesItem?.duration}`;
        }
    }
    return seriesType;
  };

  return (
    <Container className={className}>
      <Icon>{renderIcon()}</Icon>
      <SeriesType>{renderText()}</SeriesType>
    </Container>
  );
};

SeriesArticleItemType.displayName = "SeriesArticleItemType";
